import { Question1 } from "../components/complaint_checker/question1";
import { Question2 } from "../components/complaint_checker/question2";
import { Question3 } from "../components/complaint_checker/question3";
import { Question4 } from "../components/complaint_checker/question4";
import { Question5 } from "../components/complaint_checker/question5";
import { Question6 } from "../components/complaint_checker/question6";
import { Question7 } from "../components/complaint_checker/question7";
import { useState, useEffect } from "react";
import PreSubmission from "../components/complaint_checker/preSubmission";
import { Stepper } from "../components/common";
import "./ComplaintChecker.scss";
import { useParentBridge } from "../hooks/useParentBridge";

const STORAGE_KEY = "complaintChecker";

// Helper to load saved state from sessionStorage
const loadSavedState = () => {
  const saved = sessionStorage.getItem(STORAGE_KEY);
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch {
      return null;
    }
  }
  return null;
};

export const ComplaintChecker = () => {
  const savedState = loadSavedState();
  const { postToParent } = useParentBridge();

  const [question, setQuestion] = useState(savedState?.currentQuestion || 1);
  const [furthestQuestion, setFurthestQuestion] = useState(savedState?.furthestQuestion || 1);
  const [answers, setAnswers] = useState(savedState?.answers || {});
  const [isNhsComplaint, setIsNhsComplaint] = useState(
    savedState?.answers?.q1 === "nhs"
  );
  const [jurisdiction, setJurisdiction] = useState(() => {
    const q1 = savedState?.answers?.q1;
    return q1 === "nhs" || q1 === "parliamentary" ? q1 : "";
  });

  // Persist all state to single sessionStorage key
  useEffect(() => {
    const isCompleted = question === 8;
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        currentQuestion: question,
        furthestQuestion: furthestQuestion,
        answers: answers,
        completed: isCompleted,
      })
    );
  }, [question, furthestQuestion, answers]);

  useEffect(() => {
    setTimeout(() => {
      postToParent({ type: "SCROLL_TO_TOP" });
    }, 100);
  }, [question, postToParent]);

  useEffect(() => {
    sessionStorage.setItem("complaint_checker_question", question.toString());
  }, [question]);

  const updateAnswer = (questionKey, value) => {
    const qNum = parseInt(questionKey.replace("q", ""), 10);
    setAnswers((prev) => {
      const cleared = Object.fromEntries(
        Object.entries(prev).filter(([k]) => parseInt(k.replace("q", ""), 10) <= qNum)
      );
      return { ...cleared, [questionKey]: value };
    });
    setFurthestQuestion((prev) => Math.min(prev, question));
  };

  // Callback function from child component to update our questions in the complaint checker tool
  const updateQuestion = (e) => {
    setQuestion(e);
    if (e > furthestQuestion) {
      setFurthestQuestion(e);
    }
  };

  const resetChecker = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem('complaint_checker_question');
    sessionStorage.removeItem('answer_q1');
    sessionStorage.removeItem('answer_q2');
    sessionStorage.removeItem('answer_q3');
    sessionStorage.removeItem('answer_q4');
    sessionStorage.removeItem('answer_q5');
    sessionStorage.removeItem('answer_q6');
    sessionStorage.removeItem('answer_q7');
    sessionStorage.removeItem('complaint_checker_completed');
    setQuestion(1);
    setFurthestQuestion(1);
    setAnswers({});
    setIsNhsComplaint(false);
    setJurisdiction('');
  };

  const updateJurisdiction = (newJurisdiction) => {
    if (jurisdiction && jurisdiction !== newJurisdiction) {
      setAnswers((prev) => ({ q1: prev.q1 }));
      setFurthestQuestion(1);
    }
    setJurisdiction(newJurisdiction);
  };

  const NHS_QUESTIONS = [1, 2, 4, 5, 6, 7, 8];
  const PARLIAMENTARY_QUESTIONS = [1, 2, 5, 6, 8];
  const DEFAULT_QUESTIONS = [1, 2, 3, 4, 5, 6, 7, 8];

  const activeQuestions =
    jurisdiction === "nhs"
      ? NHS_QUESTIONS
      : jurisdiction === "parliamentary"
      ? PARLIAMENTARY_QUESTIONS
      : DEFAULT_QUESTIONS;

  const currentStepPos = activeQuestions.indexOf(question) + 1;
  const stepperCurrentStep = currentStepPos > 0 ? currentStepPos : activeQuestions.length;
  const stepperFurthestStep = activeQuestions.filter((q) => q <= furthestQuestion).length;

  const handleStepperClick = (stepPos) => {
    updateQuestion(activeQuestions[stepPos - 1]);
  };

  return (
    <div className="landing-page">
      <div className="landing-content">
        <div className="landing-header">
          <p className="landing-label">Complaint checker</p>
          <h1 className="landing-title">Make a new complaint</h1>
        </div>

        <Stepper
          totalSteps={activeQuestions.length}
          currentStep={stepperCurrentStep}
          furthestStep={stepperFurthestStep}
          onStepClick={handleStepperClick}
        />
        <div className="question-page__divider" />

        {(() => {
          const questionComponents = {
            1: (
              <Question1
                updateQuestion={updateQuestion}
                updateJurisdiction={updateJurisdiction}
                setNhsComplaint={setIsNhsComplaint}
                answer={answers.q1}
                updateAnswer={(val) => updateAnswer("q1", val)}
              />
            ),
            2: (
              <Question2
                updateQuestion={updateQuestion}
                jurisdiction={jurisdiction}
                answer={answers.q2}
                updateAnswer={(val) => updateAnswer("q2", val)}
              />
            ),
            3: (
              <Question3
                updateQuestion={updateQuestion}
                jurisdiction={jurisdiction}
                answer={answers.q3}
                updateAnswer={(val) => updateAnswer("q3", val)}
              />
            ),
            4: (
              <Question4
                updateQuestion={updateQuestion}
                jurisdiction={jurisdiction}
                answer={answers.q4}
                updateAnswer={(val) => updateAnswer("q4", val)}
              />
            ),
            5: (
              <Question5
                updateQuestion={updateQuestion}
                jurisdiction={jurisdiction}
                answer={answers.q5}
                updateAnswer={(val) => updateAnswer("q5", val)}
              />
            ),
            6: (
              <Question6
                updateQuestion={updateQuestion}
                jurisdiction={jurisdiction}
                answer={answers.q6}
                updateAnswer={(val) => updateAnswer("q6", val)}
              />
            ),
            7: <Question7 updateQuestion={updateQuestion} />,
            8: (
              <PreSubmission
                isNhsComplaint={isNhsComplaint}
                updateQuestion={updateQuestion}
                answers={answers}
                previousQuestion={activeQuestions[activeQuestions.indexOf(8) - 1]}
                onReset={resetChecker}
              />
            ),
          };

          return questionComponents[question] || null;
        })()}
      </div>
    </div>
  );
};
